import { useState, useEffect, FormEvent } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { backendApi } from '../services/auth0-api';

function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [validationErrors, setValidationErrors] = useState({
    password: '',
    confirmPassword: '',
  });

  const email = searchParams.get('email') || '';
  const token = searchParams.get('token') || '';

  useEffect(() => {
    if (!email || !token) {
      setError('Invalid reset link. Please request a new password reset.');
    }
  }, [email, token]);

  const validatePassword = (value: string) => {
    if (!value) {
      return 'Password is required';
    }
    if (value.length < 8) {
      return 'Password must be at least 8 characters';
    }

    const hasLowerCase = /[a-z]/.test(value);
    const hasUpperCase = /[A-Z]/.test(value);
    const hasNumbers = /[0-9]/.test(value);
    const hasSpecialChars = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(value);

    const characterTypeCount = [hasLowerCase, hasUpperCase, hasNumbers, hasSpecialChars].filter(Boolean).length;

    if (characterTypeCount < 3) {
      return 'Password must contain at least 3 of: lowercase, uppercase, numbers, special characters';
    }

    return '';
  };

  const validateConfirmPassword = (value: string) => {
    if (!value) {
      return 'Please confirm your password';
    }
    if (value !== password) {
      return 'Passwords do not match';
    }
    return '';
  };

  const handlePasswordChange = (value: string) => {
    setPassword(value);
    if (value) {
      setValidationErrors((prev) => ({ ...prev, password: validatePassword(value) }));
    } else {
      setValidationErrors((prev) => ({ ...prev, password: '' }));
    }
    if (confirmPassword) {
      setValidationErrors((prev) => ({
        ...prev,
        confirmPassword: value !== confirmPassword ? 'Passwords do not match' : '',
      }));
    }
  };

  const handleConfirmPasswordChange = (value: string) => {
    setConfirmPassword(value);
    if (value) {
      setValidationErrors((prev) => ({
        ...prev,
        confirmPassword: validateConfirmPassword(value),
      }));
    } else {
      setValidationErrors((prev) => ({ ...prev, confirmPassword: '' }));
    }
  };

  const isFormValid = () => {
    return (
      password &&
      confirmPassword &&
      !validatePassword(password) &&
      !validateConfirmPassword(confirmPassword)
    );
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');

    if (!isFormValid()) {
      setError('Please fix all validation errors before submitting');
      return;
    }

    setLoading(true);

    try {
      await backendApi.resetPassword(email, token, password);
      setSuccess(true);
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="signin-container">
      <div className="signin-form">
        <h2>Set New Password</h2>

        {success ? (
          <div>
            <div style={{ color: 'green', fontSize: '14px', marginBottom: '20px', padding: '15px', backgroundColor: '#d4edda', borderRadius: '4px' }}>
              ✓ Password reset successfully! Redirecting to sign in...
            </div>
            <Link to="/login" style={{ color: '#007bff', textDecoration: 'underline' }}>
              Go to Sign In
            </Link>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <p style={{ color: '#666', marginBottom: '20px' }}>
              Enter your new password below.
            </p>

            <label htmlFor="password">New Password</label>
            <input
              id="password"
              type="password"
              placeholder="Min 8 chars with 3 of: lowercase, uppercase, numbers, special chars"
              value={password}
              onChange={(e) => handlePasswordChange(e.target.value)}
              required
              disabled={loading || !email || !token}
              style={{ borderColor: validationErrors.password ? '#dc3545' : undefined }}
            />
            {password && (
              <div style={{ fontSize: '11px', marginTop: '4px', color: '#6c757d' }}>
                <div>✓ At least 8 characters: {password.length >= 8 ? '✓' : '✗'}</div>
                <div>✓ Contains 3 of 4 types (lowercase, uppercase, numbers, special): {
                  (() => {
                    const hasLower = /[a-z]/.test(password);
                    const hasUpper = /[A-Z]/.test(password);
                    const hasNum = /[0-9]/.test(password);
                    const hasSpecial = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password);
                    const count = [hasLower, hasUpper, hasNum, hasSpecial].filter(Boolean).length;
                    return count >= 3 ? '✓' : `${count}/3`;
                  })()
                }</div>
              </div>
            )}
            {validationErrors.password && (
              <div style={{ color: '#dc3545', fontSize: '12px', marginTop: '4px' }}>
                {validationErrors.password}
              </div>
            )}

            <label htmlFor="confirmPassword">Confirm New Password</label>
            <input
              id="confirmPassword"
              type="password"
              placeholder="Confirm your new password"
              value={confirmPassword}
              onChange={(e) => handleConfirmPasswordChange(e.target.value)}
              required
              disabled={loading || !email || !token}
              style={{ borderColor: validationErrors.confirmPassword ? '#dc3545' : undefined }}
            />
            {validationErrors.confirmPassword && (
              <div style={{ color: '#dc3545', fontSize: '12px', marginTop: '4px' }}>
                {validationErrors.confirmPassword}
              </div>
            )}

            {error && (
              <div style={{ color: 'red', fontSize: '14px', marginBottom: '10px' }}>
                {error}
              </div>
            )}

            <button type="submit" className="continue-btn" disabled={loading || !isFormValid() || !email || !token}>
              {loading ? 'Resetting...' : 'Reset Password'}
            </button>

            <div className="login-link">
              <Link to="/login">Back to Sign In</Link>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

export default ResetPassword;
